import type { TenantUsageSummaryResponse } from '@contract-analyzer/api-client';
import { type ReactNode, useContext } from 'react';
import { NavLink } from 'react-router';

import { canSubmitChargeCodes } from '@/components/charge-codes/charge-code-utils';
import { QuotaRunway } from '@/components/quota-runway';
import { Progress } from '@/components/ui/progress';
import { TenantCtx } from '@/hooks/tenant-context';
import { useAuth } from '@/hooks/use-auth';
import {
  useExtractionRunway,
  type ExtractionRunwaySnapshot,
  type ExtractionRunwayState,
} from '@/hooks/use-extraction-runway';
import { useNow } from '@/hooks/use-now';
import { useTrialEndedNotice } from '@/hooks/use-trial-ended-notice';
import { formatCount } from '@/lib/format/count';
import { billingSettingsPath } from '@/lib/routes';
import { formatTrialDate, formatTrialEndsValue, type TrialPhase } from '@/lib/trial-phase';

function quotaPercent(used: number, quota: number): number {
  if (quota <= 0) return 0;
  return Math.min(100, Math.round((used / quota) * 100));
}

/**
 * Below this many remaining extractions the widget starts pointing at the
 * path forward. The threshold is intentionally in remaining units, not a
 * percentage: the quota is small and admin-editable, and "2 left" means the
 * same thing whether the workspace started with 10 or 40.
 */
const LOW_RUNWAY_THRESHOLD = 2;

function runwayCaption(runway: ExtractionRunwaySnapshot): string {
  if (runway.remaining === runway.quota) {
    return `${formatCount(runway.quota)} included`;
  }
  if (runway.remaining === 0) {
    return `All ${formatCount(runway.quota)} used`;
  }
  return `${formatCount(runway.remaining)} left`;
}

type ChargeCodeReadiness = TenantUsageSummaryResponse['charge_code_status'];

function ChargeCodeLink({ billingPath, label }: { readonly billingPath: string; readonly label: string }) {
  return (
    <NavLink className="text-accent hover:underline" to={billingPath}>
      {label}
    </NavLink>
  );
}

/**
 * The line under a quota-limited runway. Only speaks when there is something
 * to say: a trial that just ended (so the allowance appearing is explained),
 * a runway that is nearly spent, or a charge code already on its way.
 */
function RunwayHint({
  runway,
  chargeCodeReadiness,
  trial,
  canSubmitChargeCode,
  billingPath,
  now,
}: {
  readonly runway: ExtractionRunwaySnapshot;
  readonly chargeCodeReadiness: ChargeCodeReadiness;
  readonly trial: TrialPhase;
  readonly canSubmitChargeCode: boolean;
  readonly billingPath: string;
  readonly now: Date;
}) {
  const trialEnded = trial.kind === 'ended' ? `Trial ended ${formatTrialDate(trial.endedAt, now)}. ` : '';
  const low = runway.remaining <= LOW_RUNWAY_THRESHOLD;

  if (chargeCodeReadiness === 'pending') {
    return (
      <p className="text-xs leading-4 text-muted-foreground">
        {trialEnded}Charge code pending approval — extractions resume once it clears.
      </p>
    );
  }
  if (!low) {
    return trialEnded ? <p className="text-xs leading-4 text-muted-foreground">{trialEnded.trim()}</p> : null;
  }

  const exhausted = runway.remaining === 0;
  const tail = exhausted ? ' to run more.' : ' to keep extractions running.';
  // A declined code is not "no code": the person who submitted it needs to
  // know a different one (or a corrected one, via an admin) is what is wanted.
  const verb = chargeCodeReadiness === 'rejected' ? 'Submit a different charge code' : 'Add a charge code';
  return (
    <p className="text-xs leading-4 text-muted-foreground">
      {trialEnded}
      {chargeCodeReadiness === 'rejected' ? 'Charge code rejected. ' : null}
      {exhausted ? 'Results stay available. ' : null}
      {canSubmitChargeCode ? (
        <>
          <ChargeCodeLink billingPath={billingPath} label={verb} />
          {tail}
        </>
      ) : (
        <>A workspace admin can {verb.toLowerCase()}{tail}</>
      )}
    </p>
  );
}

function FreemiumRunwaySection({
  runway,
  chargeCodeReadiness,
  trial,
  docSuffix,
  canSubmitChargeCode,
  billingPath,
  now,
}: {
  readonly runway: ExtractionRunwaySnapshot;
  readonly chargeCodeReadiness: ChargeCodeReadiness;
  readonly trial: TrialPhase;
  readonly docSuffix: string;
  readonly canSubmitChargeCode: boolean;
  readonly billingPath: string;
  readonly now: Date;
}) {
  return (
    <>
      <QuotaRunway quota={runway.quota} used={runway.used} />
      <p className="text-xs tabular-nums text-muted-foreground">
        {runwayCaption(runway)}
        {docSuffix}
      </p>
      <RunwayHint
        runway={runway}
        chargeCodeReadiness={chargeCodeReadiness}
        trial={trial}
        canSubmitChargeCode={canSubmitChargeCode}
        billingPath={billingPath}
        now={now}
      />
    </>
  );
}

/**
 * While a trial runs the workspace is paid and its allowance does not apply,
 * so the sidebar says only when the trial ends: a date until the final week
 * and a countdown inside it. The hint appears only once the end is close
 * enough to act on, and it follows the charge-code posture: a submitted code
 * is reassurance, a rejected one needs a different code, and only "none" asks
 * for a first one.
 */
function TrialEndsHint({
  trial,
  chargeCodeReadiness,
  canSubmitChargeCode,
  billingPath,
  now,
}: {
  readonly trial: Extract<TrialPhase, { kind: 'active' }>;
  readonly chargeCodeReadiness: ChargeCodeReadiness;
  readonly canSubmitChargeCode: boolean;
  readonly billingPath: string;
  readonly now: Date;
}) {
  if (!trial.isFinalWeek) return null;
  const after = `carry on after ${formatTrialDate(trial.endsAt, now)}.`;
  if (chargeCodeReadiness === 'pending') {
    return (
      <p className="text-xs leading-4 text-muted-foreground">
        Charge code pending approval — once it clears, work will {after}
      </p>
    );
  }
  const verb = chargeCodeReadiness === 'rejected' ? 'Submit a different charge code' : 'Add a charge code';
  return (
    <p className="text-xs leading-4 text-muted-foreground">
      {chargeCodeReadiness === 'rejected' ? 'Charge code rejected. ' : null}
      {canSubmitChargeCode ? (
        <>
          <ChargeCodeLink billingPath={billingPath} label={verb} /> to {after}
        </>
      ) : (
        <>
          A workspace admin can {verb.toLowerCase()} to {after}
        </>
      )}
    </p>
  );
}

/** Legacy document-quota meter for workspaces without an extraction quota. */
function DocumentQuotaSection({ usage }: { readonly usage: TenantUsageSummaryResponse }) {
  const docQuota = usage.document_quota;
  if (docQuota <= 0 && usage.extraction_quota <= 0) {
    return (
      <p className="text-xs tabular-nums text-muted-foreground">
        {`${formatCount(usage.documents_uploaded)} docs · ${formatCount(usage.extractions_completed)} extractions`}
      </p>
    );
  }
  return (
    <>
      <Progress
        aria-label={`Workspace usage: ${String(quotaPercent(usage.documents_uploaded, docQuota))}%`}
        className="h-1.5 ring-1 ring-border"
        value={quotaPercent(usage.documents_uploaded, docQuota)}
      />
      <p className="text-xs tabular-nums text-muted-foreground">
        {`${formatCount(usage.documents_uploaded)}/${formatCount(docQuota)} docs`}
      </p>
    </>
  );
}

function UsageBlock({ label, children }: { readonly label?: string; readonly children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 px-2 group-data-[collapsible=icon]:hidden">
      {label === undefined ? null : <p className="text-micro font-medium text-muted-foreground">{label}</p>}
      {children}
    </div>
  );
}

/** The allowance a workspace without a charge code or trial spends from. */
function AllowanceBlock({
  state,
  billingPath,
  canSubmitChargeCode,
  now,
}: {
  readonly state: Extract<ExtractionRunwayState, { status: 'ready' }>;
  readonly billingPath: string;
  readonly canSubmitChargeCode: boolean;
  readonly now: Date;
}) {
  const { chargeCodeReadiness, runway, usage, trial } = state;
  if (runway === null) {
    return (
      <UsageBlock label="Usage">
        <DocumentQuotaSection usage={usage} />
      </UsageBlock>
    );
  }
  const docQuota = usage.document_quota;
  const docSuffix =
    docQuota > 0
      ? ` · ${formatCount(usage.documents_uploaded)}/${formatCount(docQuota)} docs`
      : '';
  return (
    <UsageBlock label="Free extractions">
      <FreemiumRunwaySection
        runway={runway}
        chargeCodeReadiness={chargeCodeReadiness}
        trial={trial}
        docSuffix={docSuffix}
        canSubmitChargeCode={canSubmitChargeCode}
        billingPath={billingPath}
        now={now}
      />
    </UsageBlock>
  );
}

export function SidebarUsageWidget({ now: fixedNow }: { readonly now?: Date } = {}) {
  // The rail shows the time to the minute; the runway and the notice read the
  // clock themselves when they fetch and announce, unless a test pins it.
  const now = useNow(fixedNow);
  const tenant = useContext(TenantCtx);
  const { hasPermission, user } = useAuth();
  const canView = hasPermission('usage.view');
  const state = useExtractionRunway({ enabled: tenant !== null && canView, now: fixedNow });
  useTrialEndedNotice({
    state,
    tenantId: tenant?.tenantId ?? null,
    userId: user?.user_id ?? null,
    now: fixedNow,
  });

  if (tenant === null || !canView) return null;
  return (
    <SidebarUsageSummary
      state={state}
      billingPath={billingSettingsPath(tenant.slug)}
      canSubmitChargeCode={canSubmitChargeCodes(user)}
      now={now}
    />
  );
}

/**
 * The sidebar speaks about usage only when it asks something of the reader:
 * the free allowance they spend from, or when a trial ends. A workspace on a
 * charge code has nothing to act on here, so it shows nothing; its totals are
 * in Settings. Nor does a load or a failed fetch take space: most workspaces
 * are paid, and a placeholder they then took away would make the sidebar's
 * foot jump.
 */
export function SidebarUsageSummary({
  state,
  billingPath,
  canSubmitChargeCode,
  now,
}: {
  readonly state: ExtractionRunwayState;
  readonly billingPath: string;
  readonly canSubmitChargeCode: boolean;
  readonly now: Date;
}) {
  if (state.status !== 'ready') return null;
  if (!state.isPaidWorkspace) {
    return (
      <AllowanceBlock
        state={state}
        billingPath={billingPath}
        canSubmitChargeCode={canSubmitChargeCode}
        now={now}
      />
    );
  }
  const { trial } = state;
  if (trial.kind !== 'active') return null;
  return (
    <UsageBlock>
      <dl className="flex flex-col">
        <UsageRow label="Trial ends" value={formatTrialEndsValue(trial, now)} />
      </dl>
      <TrialEndsHint
        trial={trial}
        chargeCodeReadiness={state.chargeCodeReadiness}
        canSubmitChargeCode={canSubmitChargeCode}
        billingPath={billingPath}
        now={now}
      />
    </UsageBlock>
  );
}

function UsageRow({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2 text-xs leading-5">
      <dt className="truncate text-muted-foreground">{label}</dt>
      <dd className="shrink-0 font-medium tabular-nums text-foreground">{value}</dd>
    </div>
  );
}
